import { Button, CollapsibleCard, useToast } from '@citizen-walk/shared-ui';
import {
  buildShortLinkUrl,
  pickParticipantQrShortLink,
  type District,
  type ShortLinkSummary,
} from '@citizen-walk/shared-types';
import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { useSessions } from '../sessions-admin';

/**
 * 参加者向け URL / QR / PDF 印刷 panel (Sprint BG-DataSettings 15、GAP-A7 完全踏襲)。
 *
 * 旧 v1 `old/apps/admin/src/pages/DistrictDetailPage.tsx` (line 1556-1649 の UrlQrRow + 上位 URL 配布
 * セクション踏襲)。既存の minimum `DistrictAccessPanel` (api.qrserver.com 外部 service 利用) を
 * 置き換える上位 panel として、admin app 内で qrcode npm パッケージで生成し、外部依存を排除。
 *
 * 踏襲した点 (旧 v1):
 *   - 参加 URL = `<origin>/m/{urlToken}`
 *   - QR 生成は qrcode lib (errorCorrectionLevel='M'、width=360、margin=2)
 *   - 「コピー」「QR PNG ダウンロード」「印刷」の 3 ボタン
 *   - QR 切替時に urlChange で再生成 (旧 useEffect 同等)
 *
 * 変えた点 (v2):
 *   - 外部 service `api.qrserver.com` 廃止 → qrcode lib (Sprint BG 14 で追加済) で内製化、CSP 緩和不要
 *   - サイズ変更 select (180/240/360 px) — 旧は 360 固定
 *   - PDF 印刷専用 CSS (@page A4 portrait + .print-only) で 1 枚出力
 *
 * 廃止した点:
 *   - 旧 hardcoded copiedKey の global state (本 panel は self-contained で 'copied' bool 管理)
 *
 * 2026-09-04 の追加 (セッションごとの参加 QR):
 *   - 配布対象を「全体共通」またはセッション 1 件から選べるようにした。セッションを選ぶと URL は
 *     `/m/{urlToken}/join/{sessionId}` になり、読み取った人はそのセッションが選択された状態で参加できる
 *     (未参加者はユーザー設定で初期選択、参加済みの人は現在のセッションとして適用される)。
 *   - ダウンロードする PNG に「地区名」と「全体共通 / セッション名」を焼き込む。印刷物や配布画像だけを
 *     見てどの QR か分かるようにするため (canvas が使えない環境では QR 単体に fallback)。
 *
 * 2026-10-04 の追加 (短縮URLの併記):
 *   - 短縮アドレス管理で「参加者用の URL と QR コードに短縮URLを表示する」にチェックがあり、有効期間内で、
 *     配布対象 (全体共通 / セッション) が一致する短縮アドレスがあれば、URL 欄・QR 画像 (PNG)・印刷物に
 *     併記する。QR の中身は長い参加 URL のまま (短縮アドレスは期限付きなので、QR は期限に依存させない)。
 */

export interface DistrictUrlQrPanelProps {
  district: District;
  /** 参加者 deep link の URL origin (例: `https://app.citizen-walk.example`)。未指定なら window.location.origin。 */
  participantUrlBase?: string;
  /** 地区の短縮アドレス一覧 (併記対象の選定に使う)。未指定 = 併記しない。 */
  shortLinks?: readonly ShortLinkSummary[];
}

/** pure: deep link URL を組み立てる (unit test 可能化)。 */
export function buildParticipantUrl(urlToken: string, origin: string): string {
  const safeOrigin = origin.replace(/\/+$/, '');
  return `${safeOrigin}/m/${encodeURIComponent(urlToken)}`;
}

/** pure: セッションごとの参加 deep link を組み立てる (2026-09-04)。user 側 `/m/:urlToken/join/:sessionId`。 */
export function buildSessionJoinUrl(urlToken: string, sessionId: string, origin: string): string {
  const safeOrigin = origin.replace(/\/+$/, '');
  return `${safeOrigin}/m/${encodeURIComponent(urlToken)}/join/${encodeURIComponent(sessionId)}`;
}

/** pure: 配布対象の表示名。セッション未選択は「全体共通」(2026-09-04)。 */
export function buildQrTargetLabel(sessionName: string | null | undefined): string {
  const name = (sessionName ?? '').trim();
  return name ? name : '全体共通';
}

/** pure: PDF 印刷用ファイル名 (district 名 + token 短縮 + 対象、illegal char除去)。 */
export function buildQrFilename(
  districtName: string,
  urlToken: string,
  sessionName?: string | null,
): string {
  const sanitize = (v: string) => v.replace(/[^\w぀-ヿ㐀-鿿ー]/g, '_').slice(0, 20);
  const safeName = sanitize(districtName);
  const shortToken = urlToken.slice(0, 8);
  const target = (sessionName ?? '').trim();
  const suffix = target ? `_${sanitize(target)}` : '';
  return `${safeName || 'district'}_${shortToken}${suffix}_qr`;
}

/**
 * QR 画像の下に「地区名 / 配布対象」を焼き込んだ PNG dataURL を作る (2026-09-04)。
 * `shortUrl` を渡すと、その下に「短縮URL: …」も焼き込む (2026-10-04)。
 *
 * user 側 `features/auth/qr.ts` の焼込みと同じ考え方 (canvas で合成)。canvas が使えない
 * (SSR / jsdom) 場合は QR 単体の dataURL をそのまま返し、機能を落とさない。
 */
export async function composeQrWithLabel(
  qrDataUrl: string,
  qrSize: number,
  districtName: string,
  targetLabel: string,
  shortUrl?: string | null,
): Promise<string> {
  if (typeof document === 'undefined') return qrDataUrl;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return qrDataUrl;

  const img = await new Promise<HTMLImageElement | null>((resolve) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => resolve(null);
    el.src = qrDataUrl;
  });
  if (!img) return qrDataUrl;

  const padX = 16;
  const titleH = 34;
  const targetH = 30;
  const shortH = shortUrl ? 26 : 0;
  const totalW = qrSize + padX * 2;
  const totalH = titleH + qrSize + targetH + shortH + 10;
  canvas.width = totalW;
  canvas.height = totalH;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, totalW, totalH);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#0f172a';
  ctx.font =
    'bold 17px system-ui, -apple-system, "Segoe UI", "Hiragino Sans", "Yu Gothic", sans-serif';
  ctx.fillText(districtName, totalW / 2, titleH / 2 + 4, totalW - padX * 2);

  ctx.drawImage(img, padX, titleH, qrSize, qrSize);

  ctx.fillStyle = '#0F766E';
  ctx.font = '15px system-ui, -apple-system, "Segoe UI", "Hiragino Sans", "Yu Gothic", sans-serif';
  ctx.fillText(targetLabel, totalW / 2, titleH + qrSize + targetH / 2 + 4, totalW - padX * 2);

  if (shortUrl) {
    // 長いと fillText の maxWidth で横に縮む (切れずに全体が入る)。
    ctx.fillStyle = '#0f172a';
    ctx.font =
      'bold 13px system-ui, -apple-system, "Segoe UI", "Hiragino Sans", "Yu Gothic", sans-serif';
    ctx.fillText(
      `短縮URL: ${shortUrl}`,
      totalW / 2,
      titleH + qrSize + targetH + shortH / 2,
      totalW - padX * 2,
    );
  }

  return canvas.toDataURL('image/png');
}

/** valid QR size (旧 v1 と同 set)。 */
export const QR_SIZE_OPTIONS = [180, 240, 360] as const;
export type QrSize = (typeof QR_SIZE_OPTIONS)[number];

export function DistrictUrlQrPanel({
  district,
  participantUrlBase,
  shortLinks,
}: DistrictUrlQrPanelProps) {
  const { toast } = useToast();
  const [qrSize, setQrSize] = useState<QrSize>(240);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  // 2026-09-04: 配布対象 ('' = 全体共通 / それ以外はセッション ID)。
  const [targetSessionId, setTargetSessionId] = useState<string>('');
  const sessionsState = useSessions(district.id);
  const sessions = useMemo(
    () =>
      sessionsState.status === 'ready' ? sessionsState.items.filter((s) => !s.archivedAt) : [],
    [sessionsState],
  );
  const targetSessionName = useMemo(
    () => sessions.find((s) => s.id === targetSessionId)?.name ?? null,
    [sessions, targetSessionId],
  );
  const targetLabel = buildQrTargetLabel(targetSessionName);

  const origin = useMemo(() => {
    if (participantUrlBase) return participantUrlBase;
    if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
    return '';
  }, [participantUrlBase]);

  const participantUrl = useMemo(
    () =>
      targetSessionId
        ? buildSessionJoinUrl(district.urlToken, targetSessionId, origin)
        : buildParticipantUrl(district.urlToken, origin),
    [district.urlToken, origin, targetSessionId],
  );

  // 2026-10-04: 併記する短縮URL (管理側で表示指定 + 有効期間内 + 配布対象一致のもの)。
  const shortUrl = useMemo(() => {
    const link = pickParticipantQrShortLink(shortLinks ?? [], targetSessionId || null);
    return link ? buildShortLinkUrl(origin, link.code) : null;
  }, [shortLinks, targetSessionId, origin]);

  // QR 生成 (qrcode lib、Sprint BG 14 で追加済)。url / size / 配布対象 / 地区名 変更時に再生成。
  // 2026-09-04: 生成後に地区名と配布対象を焼き込む (canvas 不可の環境では QR 単体のまま)。
  useEffect(() => {
    let cancelled = false;
    setQrError(null);
    QRCode.toDataURL(participantUrl, {
      errorCorrectionLevel: 'M',
      width: qrSize,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((d) => composeQrWithLabel(d, qrSize, district.name, targetLabel, shortUrl))
      .then((d) => {
        if (!cancelled) setQrDataUrl(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setQrDataUrl(null);
          setQrError((e as Error)?.message ?? 'QR 生成失敗');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [participantUrl, qrSize, district.name, targetLabel, shortUrl]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(participantUrl);
      toast('success', `参加 URL をコピーしました (${targetLabel})`);
    } catch (e) {
      toast('error', `コピーに失敗: ${(e as Error).message}`);
    }
  }

  function handleDownloadQr() {
    if (!qrDataUrl) {
      toast('error', 'QR がまだ生成されていません');
      return;
    }
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `${buildQrFilename(district.name, district.urlToken, targetSessionName)}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function handlePrint() {
    if (typeof window === 'undefined') return;
    window.print();
  }

  return (
    <CollapsibleCard
      title="参加者用の URL と QR コード（配布・印刷）"
      summary="参加者に配る URL・QR コードの表示、画像の保存、A4 印刷"
      storageKey="cw-admin:district-overview:url-qr"
      testId="district-url-qr-panel"
    >
      <p className="text-xs text-slate-500 mb-3">
        参加者に配布する参加 URL と QR コードです。「印刷」で A4 1 枚に URL と QR
        を出力できます（ブラウザの印刷から PDF 保存もできます）。
      </p>

      {/* 2026-09-04 — 配布対象 (全体共通 / セッション別)。セッションを選ぶと URL・QR・画像の表記が切り替わる。 */}
      <section className="mb-4">
        <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">配布対象</h4>
        <label className="flex flex-wrap items-center gap-2 text-sm min-w-0">
          <span className="text-slate-700">この QR で参加する人:</span>
          <select
            value={targetSessionId}
            onChange={(e) => setTargetSessionId(e.target.value)}
            aria-label="配布対象"
            className="min-w-0 max-w-full border border-slate-300 rounded px-2 py-1 text-sm bg-white"
            data-testid="url-qr-target-select"
          >
            <option value="">全体共通（セッションを指定しない）</option>
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <p className="text-[11px] text-slate-500 mt-1">
          {targetSessionId
            ? 'この QR から参加した人は、選んだセッションが最初から参加セッションに入った状態になります（参加済みの人は現在のセッションが切り替わります）。'
            : 'セッションを指定しない共通の QR です。参加者は自分で参加セッションを選びます。'}
          {sessions.length === 0 && sessionsState.status === 'ready' && (
            <span className="block">
              この地区にはセッションが登録されていないため、全体共通のみ発行できます。
            </span>
          )}
        </p>
      </section>

      <section className="mb-4">
        <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">参加 URL</h4>
        <div className="flex gap-2 items-start flex-wrap">
          <code
            className="flex-1 basis-56 min-w-0 text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded break-all"
            data-testid="url-qr-participant-url"
          >
            {participantUrl}
          </code>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void handleCopy()}
              data-testid="url-qr-copy"
            >
              コピー
            </Button>
            <a
              href={participantUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-3 min-h-touch text-sm rounded border border-slate-300 hover:bg-slate-50 text-slate-700"
            >
              開く ↗
            </a>
          </div>
        </div>
        {shortUrl && (
          <div className="mt-2 flex gap-2 items-start flex-wrap" data-testid="url-qr-short-url">
            <span className="text-xs font-bold text-slate-600 py-2">短縮URL</span>
            <code className="flex-1 min-w-0 text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded break-all">
              {shortUrl}
            </code>
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-1">
          参加URLトークン: <code className="break-all">{district.urlToken}</code>
        </p>
      </section>

      <section className="mb-4">
        <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">QR コード</h4>
        <label className="inline-flex items-center gap-2 text-xs mb-2">
          <span className="text-slate-700">サイズ:</span>
          <select
            value={qrSize}
            onChange={(e) => setQrSize(Number(e.target.value) as QrSize)}
            className="border border-slate-300 rounded px-2 py-1 text-xs"
            data-testid="url-qr-size-select"
          >
            {QR_SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}px
              </option>
            ))}
          </select>
        </label>
        <p className="text-[11px] text-slate-500 mb-2">
          画像には地区名と「{targetLabel}」{shortUrl ? '、短縮URL' : ''}が入ります。
        </p>
        {qrError ? (
          <p className="text-xs text-danger-700">QR 生成失敗: {qrError}</p>
        ) : qrDataUrl ? (
          <div className="flex gap-3 items-start flex-wrap">
            <img
              src={qrDataUrl}
              alt={`地区「${district.name}」${targetLabel} の参加 QR`}
              width={qrSize}
              className="max-w-full h-auto border border-slate-200 rounded bg-white"
              data-testid="url-qr-image"
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={handleDownloadQr}
              data-testid="url-qr-download"
            >
              ⬇ PNG ダウンロード
            </Button>
          </div>
        ) : (
          <div
            className="flex items-center justify-center text-xs text-slate-500 bg-white rounded border border-slate-200 max-w-full"
            style={{ width: qrSize, height: qrSize }}
          >
            生成中…
          </div>
        )}
      </section>

      <section className="mb-3">
        <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">PDF / 印刷</h4>
        <p className="text-xs text-slate-600 mb-2">
          「印刷」を押すとブラウザの印刷画面が開きます。「PDF として保存」を選ぶと PDF
          ファイルとして出力できます（A4 1 枚）。
        </p>
        <Button
          type="button"
          size="sm"
          variant="primary"
          onClick={handlePrint}
          data-testid="url-qr-print"
        >
          🖨 印刷 / PDF 保存
        </Button>
      </section>

      {/* 印刷用 CSS — `.print-only` ブロックは画面では非表示、印刷時のみ表示 */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 15mm; }
          body * { visibility: hidden; }
          .district-url-qr-print, .district-url-qr-print * { visibility: visible; }
          .district-url-qr-print {
            position: absolute; left: 0; top: 0; width: 100%;
            display: flex; flex-direction: column; align-items: center;
            gap: 12mm; padding: 10mm;
          }
        }
        @media screen { .district-url-qr-print { display: none; } }
      `}</style>
      <div className="district-url-qr-print" aria-hidden="true">
        <h1 style={{ fontSize: '24pt', margin: 0 }}>{district.name}</h1>
        <p style={{ fontSize: '18pt', margin: 0 }}>{targetLabel}</p>
        <p style={{ fontSize: '14pt', margin: 0 }}>参加 URL</p>
        <p style={{ fontSize: '12pt', wordBreak: 'break-all', margin: 0 }}>{participantUrl}</p>
        {shortUrl && (
          <>
            <p style={{ fontSize: '14pt', margin: 0 }}>短縮URL</p>
            <p style={{ fontSize: '16pt', fontWeight: 'bold', wordBreak: 'break-all', margin: 0 }}>
              {shortUrl}
            </p>
          </>
        )}
        {qrDataUrl && <img src={qrDataUrl} alt="参加 QR" style={{ width: '80mm' }} />}
        <p style={{ fontSize: '10pt', color: '#666', margin: 0 }}>
          スマートフォンのカメラで QR を読み取って参加できます
        </p>
      </div>
    </CollapsibleCard>
  );
}
